import { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
} from "../../components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Search,
  Shield,
  Lock,
  Edit2,
  UserCog,
  Users,
  Trash2,
  Building2,
  X,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import { useDebounce } from "../../hooks/useDebounce";
import { toUserMessage } from "../../utils/toUserMessage";
import { useToast } from "../../hooks/use-toast";
import { Skeleton } from "../../components/ui/skeleton";
import LoadingIndicator from "../../components/LoadingIndicator";
import {
  useAccessDefinition,
  useAdminUsers,
  useCreateUser,
  useDeleteUser,
  useResetUserPassword,
  useUpdateUserAccess,
  useUpdateUserClinics,
  useUpdateUserInfo,
  useUserAccess,
} from "../../hooks/user/useAdminAccess";
import Can from "../../auth/Can";
import { can, Perm } from "../../auth/permissions";
import Alert from "../../components/ui/Alert";
import { useClinics } from "../../hooks/useClinics";
import { ClinicScheduleStatusDto } from "@/src/web-api-client";
import { PasswordInput } from "../../components/user/PasswordInput";

// --- Types ---
type RoleDefinition = {
  name: string;
  displayName: string;
  description: string;
};

type PermissionDefinition = {
  key: string;
  displayName: string;
  group: string;
  description?: string | null;
};

type AccessDefinition = {
  roles: RoleDefinition[];
  permissions: PermissionDefinition[];
};

type AdminUserSummary = {
  id: string;
  userName: string;
  email: string;
  fullName?: string | null;
  departmentId?: number | null;
  roles: string[];
  permissionCount: number;
  clinicIds?: number[];
};

type ClinicDto = { id: number; name: string; code?: string };

function PermissionList({
  definitions,
  rolePermissions,
  userGrants,
  userDenies,
  effectivePermissions,
  onToggle,
}: {
  definitions: PermissionDefinition[];
  rolePermissions: string[];
  userGrants: string[];
  userDenies: string[];
  effectivePermissions: string[];
  onToggle: (key: string, checked: boolean) => void;
}) {
  const grouped = useMemo(() => {
    return definitions.reduce<Record<string, PermissionDefinition[]>>(
      (acc, perm) => {
        if (!acc[perm.group]) acc[perm.group] = [];
        acc[perm.group].push(perm);
        return acc;
      },
      {},
    );
  }, [definitions]);

  const roleSet = useMemo(() => new Set(rolePermissions), [rolePermissions]);
  const grantSet = useMemo(() => new Set(userGrants), [userGrants]);
  const denySet = useMemo(() => new Set(userDenies), [userDenies]);
  const effectiveSet = useMemo(
    () => new Set(effectivePermissions),
    [effectivePermissions],
  );

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([group, perms]) => (
        <div key={group} className="border rounded-lg bg-white shadow-sm">
          <div className="px-4 py-2 border-b bg-slate-50 text-sm font-semibold text-slate-700">
            {group}
          </div>

          <div className="divide-y">
            {perms.map((perm) => {
              const inherited = roleSet.has(perm.key);
              const granted = grantSet.has(perm.key);
              const denied = denySet.has(perm.key);
              const effective = effectiveSet.has(perm.key);

              return (
                <div
                  key={perm.key}
                  onClick={() => onToggle(perm.key, !effective)}
                  className="flex items-center justify-between px-4 py-3 gap-4 hover:bg-green-50 cursor-pointer"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="select-none text-sm font-medium text-slate-800">
                        {perm.displayName}
                      </div>

                      {inherited && (
                        <Badge
                          variant="outline"
                          className="select-none text-xs bg-slate-50"
                        >
                          Từ vai trò
                        </Badge>
                      )}
                      {granted && !inherited && (
                        <Badge
                          variant="secondary"
                          className="select-none text-xs bg-emerald-50 text-emerald-700 border border-emerald-200"
                        >
                          Gán riêng
                        </Badge>
                      )}
                      {denied && (
                        <Badge
                          variant="secondary"
                          className="select-none text-xs bg-rose-50 text-rose-700 border border-rose-200"
                        >
                          Bị chặn
                        </Badge>
                      )}
                    </div>

                    {perm.description && (
                      <div className="select-none text-xs text-slate-500">
                        {perm.description}
                      </div>
                    )}
                  </div>

                  <Can perm={Perm.User_Manage}>
                    <Checkbox
                      checked={effective}
                      onCheckedChange={(checked) =>
                        onToggle(perm.key, checked as boolean)
                      }
                      className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                  </Can>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function UserListPage() {
  const { toast } = useToast();
  const canGrantPerms = can(Perm.User_Manage);

  const [clinicQuery, setClinicQuery] = useState("");
  const { data: clinics = [], isLoading: isLoadingClinics } = useClinics(false);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 300);

  const [selectedUser, setSelectedUser] = useState<AdminUserSummary | null>(
    null,
  );
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  // Edit state (deny override)
  const [editRoles, setEditRoles] = useState<string[]>([]);
  const [rolePerms, setRolePerms] = useState<string[]>([]);
  const [userGrants, setUserGrants] = useState<string[]>([]);
  const [userDenies, setUserDenies] = useState<string[]>([]);

  const [editForm, setEditForm] = useState({
    fullName: "",
    userName: "",
    newPassword: "",
    clinicIds: [] as number[],
  });
  const [createForm, setCreateForm] = useState({
    fullName: "",
    userName: "",
    role: "",
    password: "",
    clinicIds: [] as number[],
  });

  const effectivePermissions = useMemo(() => {
    const s = new Set<string>();
    for (const p of rolePerms) s.add(p);
    for (const p of userGrants) s.add(p);
    for (const p of userDenies) s.delete(p);
    return Array.from(s);
  }, [rolePerms, userGrants, userDenies]);

  const { data: definitionData, isLoading: isDefinitionLoading } =
    useAccessDefinition();
  const { data: usersData, isLoading: isUsersLoading } = useAdminUsers(
    debouncedSearch,
    1,
    50,
  );
  const {
    data: userAccess,
    isLoading: isLoadingRole,
    error: userAccessError,
  } = useUserAccess(selectedUser?.id);

  const createUserMutation = useCreateUser();
  const updateUserInfoMutation = useUpdateUserInfo();
  const updateUserAccessMutation = useUpdateUserAccess();
  const resetUserPasswordMutation = useResetUserPassword();
  const deleteUserMutation = useDeleteUser();
  const updateUserClinicsMutation = useUpdateUserClinics();

  const rolePermSet = useMemo(
    () => new Set(userAccess?.rolePermissions),
    [userAccess?.rolePermissions],
  );

  const definition = (definitionData as AccessDefinition) ?? {
    roles: [],
    permissions: [],
  };

  const users = ((usersData as any)?.items ?? []) as AdminUserSummary[];

  const handleEditUser = (user: AdminUserSummary) => {
    setSelectedUser(user);
    setIsSheetOpen(true);
  };

  const handlePermissionToggle = (key: string, checked: boolean) => {
    if (!canGrantPerms) return;

    const inherited = rolePermSet.has(key);

    if (checked) {
      // Tick ON:
      // - remove deny
      // - nếu không inherited thì add grant
      setUserDenies((prev) => prev.filter((p) => p !== key));

      if (!inherited) {
        setUserGrants((prev) => (prev.includes(key) ? prev : [...prev, key]));
      }
      // nếu inherited: không cần add grant (vì role đã cấp)
      return;
    }

    // Untick OFF:
    if (inherited) {
      // - add deny để chặn quyền từ role
      setUserDenies((prev) => (prev.includes(key) ? prev : [...prev, key]));
      // grants thì bỏ luôn
      setUserGrants((prev) => prev.filter((p) => p !== key));
    } else {
      // - remove grant
      setUserGrants((prev) => prev.filter((p) => p !== key));
      // deny không có ý nghĩa nếu không inherited -> dọn cho sạch
      setUserDenies((prev) => prev.filter((p) => p !== key));
    }
  };

  const sameSet = (a: string[] = [], b: string[] = []) => {
    if (a.length !== b.length) return false;
    const sa = new Set(a);
    for (const x of b) if (!sa.has(x)) return false;
    return true;
  };

  const runOp = async (label: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
      toast({
        variant: "success",
        title: `${label} thành công`,
        duration: 2000,
      });
      return { ok: true };
    } catch (err: any) {
      const msg = await toUserMessage(err);
      toast({
        variant: "destructive",
        title: `${label} thất bại`,
        description: msg,
        duration: 3000,
      });
      return { ok: false };
    }
  };

  const handleSave = async () => {
    if (!selectedUser) return;

    const userId = selectedUser.id;
    const name = editForm.fullName.trim();
    const email = editForm.userName.trim();
    const newPassword = editForm.newPassword?.trim();

    try {
      const infoChanged =
        name !== (selectedUser.fullName ?? "").trim() ||
        email !== (selectedUser.email ?? "").trim();

      const accessChanged =
        !sameSet(editRoles ?? [], selectedUser.roles ?? []) ||
        !sameSet(userGrants ?? [], userAccess?.userGrants ?? []) ||
        !sameSet(userDenies ?? [], userAccess?.userDenies ?? []);

      const tasks: Promise<{ ok: boolean }>[] = [];

      // 1) Chỉ gọi khi có thay đổi info
      if (infoChanged) {
        if (!name || !email) {
          toast({
            variant: "destructive",
            title: "Thiếu thông tin",
            description: "Vui lòng nhập đầy đủ họ tên và tên đăng nhập.",
            duration: 2000,
          });
          return;
        }

        tasks.push(
          runOp("Cập nhật thông tin", () =>
            updateUserInfoMutation.mutateAsync({ userId, name, email }),
          ),
        );
      }

      // 2) Chỉ gọi khi có password mới
      if (newPassword) {
        tasks.push(
          runOp("Đổi mật khẩu", () =>
            resetUserPasswordMutation.mutateAsync({ userId, newPassword }),
          ),
        );
      }

      // 3) Chỉ gọi khi có thay đổi quyền/role
      if (accessChanged) {
        tasks.push(
          runOp("Cập nhật quyền", () =>
            updateUserAccessMutation.mutateAsync({
              userId,
              roles: editRoles,
              grants: userGrants,
              denies: userDenies,
            }),
          ),
        );
      }

      tasks.push(
        runOp("Cập nhật phòng khám", () =>
          updateUserClinicsMutation.mutateAsync({
            userId,
            clinicIds: editForm.clinicIds ?? [],
          }),
        ),
      );

      if (tasks.length === 0) {
        return;
      }

      const results = await Promise.all(tasks);
    } catch (error: any) {
      const msg = await toUserMessage(error);
      toast({
        variant: "destructive",
        title: "Thất bại",
        description: msg,
        duration: 2000,
      });
    }
  };

  const filteredUsers = users;

  const isSaving =
    updateUserInfoMutation.isPending ||
    updateUserAccessMutation.isPending ||
    resetUserPasswordMutation.isPending;

  const handleCreateUser = async () => {
    if (
      !createForm.fullName.trim() ||
      !createForm.userName.trim() ||
      !createForm.role.trim() ||
      !createForm.password.trim()
    ) {
      toast({
        variant: "destructive",
        title: "Thiếu thông tin",
        description:
          "Vui lòng nhập đầy đủ họ tên, tên đăng nhập, vai trò và mật khẩu.",
        duration: 2000,
      });
      return;
    }

    try {
      await createUserMutation.mutateAsync({
        name: createForm.fullName.trim(),
        email: createForm.userName.trim(),
        role: createForm.role.trim(),
        password: createForm.password.trim(),
        clinicIds: createForm.clinicIds ?? [],
      });

      setIsCreateOpen(false);
      setCreateForm({
        fullName: "",
        userName: "",
        role: "",
        password: "",
        clinicIds: [],
      });

      toast({
        variant: "success",
        title: "Thành công",
        description: "Đã tạo người dùng mới.",
        duration: 2000,
      });
    } catch (error: any) {
      const errorMsg = await toUserMessage(error);

      toast({
        variant: "destructive",
        title: "Tạo người dùng thất bại",
        description: errorMsg,
        duration: 2000,
      });
    }
  };

  const handleDeleteUser = async (user: AdminUserSummary) => {
    const confirmed = window.confirm(
      `Bạn có chắc muốn xóa người dùng ${user.fullName ?? user.userName}?`,
    );
    if (!confirmed) return;

    try {
      await deleteUserMutation.mutateAsync(user.id);

      toast({
        variant: "success",
        title: "Đã xóa người dùng",
        duration: 2000,
      });
    } catch (error: any) {
      const errorMsg = await toUserMessage(error);

      toast({
        variant: "destructive",
        title: "Xóa người dùng thất bại",
        description: errorMsg,
        duration: 2000,
      });
    }
  };

  useEffect(() => {
    if (!selectedUser) return;

    setEditForm({
      fullName: selectedUser.fullName ?? "",
      userName: selectedUser.userName ?? selectedUser.email ?? "",
      newPassword: "",
      clinicIds: selectedUser.clinicIds ?? [],
    });

    setEditRoles(selectedUser.roles ?? []);
    setRolePerms([]);
    setUserGrants([]);
    setUserDenies([]);
  }, [selectedUser]);

  useEffect(() => {
    if (!userAccess) return;
    setEditRoles((userAccess as any).roles ?? []);
    setRolePerms((userAccess as any).rolePermissions ?? []);
    setUserGrants((userAccess as any).userGrants ?? []);
    setUserDenies((userAccess as any).userDenies ?? []);
  }, [userAccess]);

  useEffect(() => {
    if (!userAccessError) return;

    const showError = async () => {
      const errorMsg = await toUserMessage(userAccessError);
      toast({
        variant: "destructive",
        title: "Load thông tin người dùng thất bại",
        description: errorMsg,
        duration: 2000,
      });
    };

    showError();
  }, [toast, userAccessError]);

  useEffect(() => {
    if (isCreateOpen) return;
    setCreateForm({
      fullName: "",
      userName: "",
      role: "",
      password: "",
      clinicIds: [],
    });
  }, [isCreateOpen]);

  useEffect(() => {
    const firstRole = definition.roles?.[0]?.name;
    if (!createForm.role && firstRole) {
      setCreateForm((prev) => ({ ...prev, role: firstRole }));
    }
  }, [definition.roles, createForm.role]);

  const activeRoleName =
    definition.roles.find((r) => r.name === editRoles[0])?.displayName ??
    editRoles[0] ??
    "Chưa chọn";

  const canManageUser = can(Perm.User_Manage);

  const toggleClinic = (clinicId: number, mode: "edit" | "create") => {
    const setForm = mode === "edit" ? setEditForm : setCreateForm;

    setForm((prev: any) => {
      const current = new Set<number>(prev.clinicIds ?? []);
      if (current.has(clinicId)) current.delete(clinicId);
      else current.add(clinicId);
      return { ...prev, clinicIds: Array.from(current) };
    });
  };

  const setAllClinics = (mode: "edit" | "create", ids: number[]) => {
    const setForm = mode === "edit" ? setEditForm : setCreateForm;
    setForm((prev: any) => ({ ...prev, clinicIds: ids }));
  };

  const clearClinics = (mode: "edit" | "create") => setAllClinics(mode, []);

  function ClinicScopePicker({
    title = "Phòng khám được phép thao tác",
    clinics,
    selectedIds,
    onChange,
    isLoading,
    className,
  }: {
    title?: string;
    clinics: ClinicScheduleStatusDto[];
    selectedIds: number[];
    onChange: (nextIds: number[]) => void;
    isLoading?: boolean;
    className?: string;
  }) {
    const [query, setQuery] = useState("");

    const safeSelectedIds = selectedIds ?? [];

    const selectedClinics = useMemo(() => {
      const map = new Map(clinics.map((c) => [c.clinicId, c]));
      return safeSelectedIds
        .map((id) => map.get(id))
        .filter(Boolean) as ClinicScheduleStatusDto[];
    }, [clinics, safeSelectedIds]);

    const filteredClinics = useMemo(() => {
      const q = query.trim().toLowerCase();
      if (!q) return clinics;
      return clinics.filter((c) => c.clinicName?.toLowerCase().includes(q));
    }, [clinics, query]);

    const toggle = (id: number) => {
      const set = new Set(safeSelectedIds);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      onChange(Array.from(set));
    };

    const selectAll = () => onChange(clinics.map((c) => c.clinicId));
    const clearAll = () => onChange([]);

    return (
      <div className={cn("space-y-4", className)}>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-brand flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            {title}
          </h3>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={selectAll}
              disabled={!!isLoading || clinics.length === 0}
            >
              Chọn tất cả
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearAll}
              disabled={(safeSelectedIds?.length ?? 0) === 0}
            >
              Bỏ chọn
            </Button>
          </div>
        </div>

        {/* Selected badges */}
        <div className="flex flex-wrap gap-2">
          {safeSelectedIds.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Chưa chọn phòng khám nào (user sẽ không thao tác được dữ liệu theo
              phòng khám).
            </p>
          ) : (
            selectedClinics.map((c) => (
              <Badge key={c.clinicId} variant="secondary" className="gap-2">
                {c.clinicName}
                <button
                  type="button"
                  className="opacity-70 hover:opacity-100"
                  onClick={() => toggle(c.clinicId!)}
                  aria-label="Bỏ chọn"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Tìm phòng khám"
            className="pl-9"
          />
        </div>

        {/* List */}
        <div className="border rounded-lg">
          <div className="max-h-[260px] overflow-y-auto p-2">
            {isLoading ? (
              <div className="p-3 text-sm text-muted-foreground">
                Đang tải danh sách phòng khám…
              </div>
            ) : (
              filteredClinics.map((c: ClinicScheduleStatusDto) => {
                const checked = safeSelectedIds.includes(c.clinicId!);
                return (
                  <label
                    key={c.clinicId}
                    className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-muted cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={checked}
                      onChange={() => toggle(c.clinicId!)}
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">
                        {c.clinicName}
                      </div>
                    </div>
                  </label>
                );
              })
            )}

            {!isLoading && clinics.length === 0 && (
              <div className="p-3 text-sm text-muted-foreground">
                Không có phòng khám.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="border-b-2 border-green-600 p-4 bg-white flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">
            Danh sách tài khoản
          </h2>
          <p className="text-slate-500">
            Quản lý người dùng, phân quyền và trạng thái hoạt động.
          </p>
        </div>
        <Can perm={Perm.User_Manage}>
          <Button
            className="btn-primary shadow-lg"
            onClick={() => setIsCreateOpen(true)}
          >
            Tạo người dùng
          </Button>
        </Can>
      </div>

      <div className="px-6 flex flex-col gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Tìm kiếm theo tên hoặc email..."
            className="pl-9"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="bg-white rounded border border-slate-200 shadow-sm overflow-hidden">
          <Table>
            <TableHeader className="bg-slate-50/50">
              <TableRow>
                <TableHead>Người dùng</TableHead>
                <TableHead>Vai trò</TableHead>
                <TableHead>Quyền hạn</TableHead>
                <Can perm={Perm.User_Manage}>
                  <TableHead className="text-center w-[100px]">
                    Thao tác
                  </TableHead>
                </Can>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isUsersLoading ? (
                Array.from({ length: 4 }).map((_, rowIndex) => (
                  <TableRow key={`skeleton-row-${rowIndex}`}>
                    {Array.from({ length: 4 }).map((_, colIndex) => (
                      <TableCell key={`skeleton-cell-${rowIndex}-${colIndex}`}>
                        <Skeleton className="h-10 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-center text-slate-400 py-8"
                  >
                    Không tìm thấy người dùng phù hợp.
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => {
                  const roleLabels = user.roles.map(
                    (role) =>
                      definition.roles.find((r) => r.name === role)
                        ?.displayName ?? role,
                  );

                  return (
                    <TableRow
                      key={user.id}
                      onClick={() => handleEditUser(user)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div>
                            <div className="font-medium text-slate-900">
                              {user.fullName || user.userName}
                            </div>
                            <div className="text-sm text-slate-500">
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        <Badge variant="default">
                          {roleLabels.join(", ") || "Chưa có"}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        {user.permissionCount > 0 ? (
                          <Badge
                            variant="secondary"
                            className="bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200"
                          >
                            <UserCog className="w-3 h-3 mr-1" />
                            Tùy chỉnh ({user.permissionCount})
                          </Badge>
                        ) : (
                          <span className="text-sm text-black">Mặc định</span>
                        )}
                      </TableCell>

                      <Can perm={Perm.User_Manage}>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleEditUser(user);
                              }}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDeleteUser(user);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </Can>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Sheet
        open={isSheetOpen}
        onOpenChange={(open) => {
          setIsSheetOpen(open);
          if (!open) {
            setSelectedUser(null);
          }
        }}
      >
        <SheetContent className="w-full sm:max-w-2xl px-0 pt-0 pb-[70px]">
          <div className="relative h-full">
            {isLoadingRole ||
              (isSaving && (
                <div className="absolute inset-0 z-50 touch-none overscroll-contain">
                  <div
                    className="absolute inset-0 bg-white/40"
                    aria-hidden="true"
                  />
                  <div className="relative w-full h-full flex items-center justify-center">
                    <LoadingIndicator />
                  </div>
                </div>
              ))}

            <div className="h-full overflow-y-auto p-6">
              <SheetHeader className="mb-4">
                <SheetTitle>Chỉnh sửa người dùng</SheetTitle>
                <SheetDescription></SheetDescription>
              </SheetHeader>
              {!canManageUser && (
                <Alert
                  variant="destructive"
                  msg="Bạn không có quyền cập nhật tài khoản"
                  className="mb-4"
                />
              )}
              {selectedUser && (
                <div className="space-y-8">
                  <div className="space-y-4">
                    <h3 className="text-base font-semibold text-brand flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Thông tin cơ bản
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Họ và tên</Label>
                        <Input
                          value={editForm.fullName}
                          onChange={(event) =>
                            setEditForm((prev) => ({
                              ...prev,
                              fullName: event.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Tên đăng nhập</Label>
                        <Input
                          value={editForm.userName}
                          onChange={(event) =>
                            setEditForm((prev) => ({
                              ...prev,
                              userName: event.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <Can admin>
                    <div className="space-y-2">
                      <Label>Mật khẩu mới</Label>
                      <PasswordInput
                        value={editForm.newPassword}
                        onChange={(event) =>
                          setEditForm((prev) => ({
                            ...prev,
                            newPassword: event.target.value,
                          }))
                        }
                        placeholder="Nhập mật khẩu mới"
                      />
                    </div>
                  </Can>

                  <Separator />

                  <div className="flex gap-8 items-center">
                    <h3 className="text-brand text-base shrink-0 font-semibold flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Vai trò
                    </h3>
                    <Select
                      value={editRoles[0] ?? ""}
                      onValueChange={(val) => setEditRoles([val])}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Chọn vai trò" />
                      </SelectTrigger>
                      <SelectContent>
                        {definition.roles.map((role) => (
                          <SelectItem key={role.name} value={role.name}>
                            {role.displayName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator />

                  <ClinicScopePicker
                    clinics={clinics}
                    selectedIds={editForm.clinicIds ?? []}
                    onChange={(next) =>
                      setEditForm((prev) => ({ ...prev, clinicIds: next }))
                    }
                    isLoading={isLoadingClinics}
                  />

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base font-semibold text-brand flex items-center gap-2">
                        <Lock className="w-4 h-4" />
                        Phân quyền tài khoản
                      </h3>
                      <Badge variant="default" className="font-normal">
                        {activeRoleName}
                      </Badge>
                    </div>

                    <PermissionList
                      definitions={definition.permissions}
                      rolePermissions={rolePerms}
                      userGrants={userGrants}
                      userDenies={userDenies}
                      effectivePermissions={effectivePermissions}
                      onToggle={handlePermissionToggle}
                    />
                  </div>
                </div>
              )}

              <SheetFooter className="mt-8 h-[60px] fixed bottom-0 flex flex-row gap-2 justify-end items-center w-full sm:max-w-2xl right-0 bg-white px-4 py-2 border-t border-t-primary">
                <SheetClose asChild>
                  <Button variant="outline">Hủy bỏ</Button>
                </SheetClose>
                <Can perm={Perm.User_Manage}>
                  <Button
                    onClick={handleSave}
                    className={cn("btn-primary", {
                      "opacity-60 cursor-not-allowed": isSaving,
                    })}
                    disabled={isSaving}
                  >
                    Lưu thay đổi
                  </Button>
                </Can>
              </SheetFooter>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <SheetContent className="w-full sm:max-w-lg px-0 pt-0 pb-[70px]">
          <div className="relative h-full">
            <div className="h-full overflow-y-auto p-6">
              <SheetHeader className="mb-6">
                <SheetTitle>Tạo người dùng</SheetTitle>
                <SheetDescription></SheetDescription>
              </SheetHeader>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label>Họ và tên</Label>
                  <Input
                    value={createForm.fullName}
                    onChange={(event) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        fullName: event.target.value,
                      }))
                    }
                    placeholder="Nhập họ tên"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Tên đăng nhập</Label>
                  <Input
                    value={createForm.userName}
                    onChange={(event) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        userName: event.target.value,
                      }))
                    }
                    placeholder="Nhập email đăng nhập"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Mật khẩu</Label>
                  
                  <PasswordInput
                    value={createForm.password}
                    onChange={(event) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        password: event.target.value,
                      }))
                    }
                    placeholder="Nhập mật khẩu"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Vai trò</Label>
                  <Select
                    value={createForm.role}
                    onValueChange={(val) =>
                      setCreateForm((prev) => ({ ...prev, role: val }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn vai trò" />
                    </SelectTrigger>
                    <SelectContent>
                      {definition.roles.map((role) => (
                        <SelectItem key={role.name} value={role.name}>
                          {role.displayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <ClinicScopePicker
                  clinics={clinics}
                  selectedIds={createForm.clinicIds ?? []}
                  onChange={(next) =>
                    setCreateForm((prev) => ({ ...prev, clinicIds: next }))
                  }
                  isLoading={isLoadingClinics}
                />
              </div>

              <SheetFooter className="mt-8 h-[60px] fixed bottom-0 flex flex-row gap-2 justify-end items-center w-full sm:max-w-lg right-0 bg-white px-4 py-2 border-t border-t-primary">
                <SheetClose asChild>
                  <Button variant="outline">Hủy bỏ</Button>
                </SheetClose>
                <Button
                  onClick={handleCreateUser}
                  className={cn("btn-primary", {
                    "opacity-60 cursor-not-allowed":
                      createUserMutation.isPending,
                  })}
                  disabled={createUserMutation.isPending}
                >
                  Tạo người dùng
                </Button>
              </SheetFooter>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
